/** Cell-value validator (TS mirror of ocr/normalizers/validator.py).
 *
 * Used by the live-validator hook to classify Birgit's input on every
 * keystroke / blur, so the cell color updates in real-time. The constants
 * inline here MUST stay in sync with:
 *   ocr/config/status_codes.json
 *   ocr/config/marginalia.json
 * If we add an /api/config endpoint later, swap to a fetched config.
 */

export type Klasse = "1" | "2" | "3" | "4" | "5a" | "5b";

export type FieldName = "von1" | "bis1" | "von2" | "bis2" | "symb";

export const TIME_FIELDS: ReadonlyArray<FieldName> = ["von1", "bis1", "von2", "bis2"] as const;
export const SYMB_FIELD: FieldName = "symb";

export const KLASSE_PRIORITY: Record<Klasse, number> = {
  "1": 0,
  "2": 1,
  "3": 2,
  "5a": 3,
  "4": 4,
  "5b": 5,
};

interface StatusCodeEntry {
  alias_of?: string;
  ignore_in_time_cell?: boolean;
}

/** Mirror of ocr/config/status_codes.json `codes` map (uppercase keys).
 *
 * Korrektur 2026-05-19 (Thomas): F ist im symb-Feld ein legitimer Status-Code
 * (User-Eingabe wurde sonst als marginalia=grau gerendert, Karte 04ebb69b
 * Tag 6). Re-classified als STATUS_CODE mit `ignore_in_time_cell: true` —
 * das macht den Code field-aware: marginalia nur wenn er in time-cell auftaucht
 * (= form-template-Leak), normaler Code im symb-Feld.
 *
 * Übrige form-template-Tokens (FASCHING/FEIERTAG/SONNTAG/FREI/LA) bleiben
 * vorerst in MARGINALIA_TOKENS — wenn User sie als symb braucht, gleiches
 * Muster anwenden.
 */
const STATUS_CODES: Record<string, StatusCodeEntry> = {
  K: {},
  KRANK: { alias_of: "K" },
  KU: {},
  U: {},
  URLAUB: { alias_of: "U" },
  DV: {},
  ZA: {},
  P: {},
  PFLEGEURLAUB: { alias_of: "P" },
  PFLEGEURL: { alias_of: "P" },
  PFLEGE: { alias_of: "P" },
  PLFU: { alias_of: "P" },
  PLF: { alias_of: "P" },
  // F = "Frei". Im symb-Feld legitim, in time-cells marginalia (Spalten-
  // header bzw. Form-Template-Leak).
  F: { ignore_in_time_cell: true },
};

/** Mirror of ocr/config/marginalia.json `tokens` PLUS marginalia_text_codes.
 * Form-template noise (printed-legend, day-name shorthands) — wenn OCR oder
 * User diese Tokens als Cell-Inhalt setzt, sind sie field-agnostisch
 * marginalia: weder in time-cells noch im symb-Feld haben sie eine legitime
 * Bedeutung. Tokens die im symb-Feld legitim sind gehören in STATUS_CODES
 * mit `ignore_in_time_cell: true` (siehe `F`). */
const MARGINALIA_TOKENS: Set<string> = new Set([
  "SON", "SONN", "SONNTAG", "DC", "PREI", "VTAG", "VTA", "TAG", "EA",
  "V.", "H.", "FT",
  "FR", "SA", "SO", "MO", "DI", "MI", "DO",
  "FASCHING", "FEIERTAG", "FREI", "LA",
]);

export interface ClassResult {
  klasse: Klasse;
  normalized?: string | null;
  redirectToField?: FieldName;
  audit?: Record<string, unknown>;
}

function normalizeToken(text: string): string {
  return (text || "").replace(/\s+/g, "").toUpperCase();
}

function isStatusCode(text: string): string | null {
  if (!text) return null;
  const t = normalizeToken(text);
  const entry = STATUS_CODES[t];
  if (!entry) return null;
  return entry.alias_of ?? t;
}

/** Field-aware: `ignore_in_time_cell` greift NUR wenn das Token in einer
 *  time-cell auftaucht. Im symb-Feld ist der Code dann legitim und der
 *  Aufrufer fällt zur Status-Code-Behandlung durch. MARGINALIA_TOKENS ist
 *  weiterhin field-agnostisch (echte form-template noise). */
function isMarginalia(text: string, field?: FieldName): boolean {
  if (!text) return false;
  const t = normalizeToken(text);
  if (MARGINALIA_TOKENS.has(t)) return true;
  // field default = treat as time-cell (rückwärts-kompatibel mit Aufrufern
  // ohne field-Argument)
  const inTimeCell = field === undefined || TIME_FIELDS.includes(field);
  if (!inTimeCell) return false;
  const entry = STATUS_CODES[t];
  if (entry?.ignore_in_time_cell) return true;
  if (entry?.alias_of) {
    const target = STATUS_CODES[entry.alias_of];
    if (target?.ignore_in_time_cell) return true;
  }
  return false;
}

const TIME_RE_STRICT = /^([01]\d|2[0-3]):[0-5]\d$/;

function isTimeStrict(text: string): boolean {
  if (!text) return false;
  return TIME_RE_STRICT.test(text.trim());
}

/** Mirror of paddle_vl._normalize_time. Returns canonical HH:MM if repairable,
 * otherwise null. */
export function normalizeTime(s: string): string | null {
  let t = (s || "").trim().replace(/\s+/g, "").replace(/\./g, ":").replace(/,/g, ":");
  if (!t) return null;
  while (t.endsWith(":")) t = t.slice(0, -1);
  let m = t.match(/^(\d{1,2}):?(\d{2})$/);
  if (m && m[1] !== undefined && m[2] !== undefined) {
    const h = parseInt(m[1], 10);
    const mm = m[2];
    if (h >= 0 && h <= 23 && parseInt(mm, 10) >= 0 && parseInt(mm, 10) <= 59) {
      return `${h.toString().padStart(2, "0")}:${mm}`;
    }
  }
  m = t.match(/^(\d{1,2})$/);
  if (m && m[1] !== undefined) {
    const h = parseInt(m[1], 10);
    if (h >= 0 && h <= 23) return `${h.toString().padStart(2, "0")}:00`;
  }
  m = t.match(/^(\d)(\d{2})$/);
  if (m && m[1] !== undefined && m[2] !== undefined) {
    const h = parseInt(m[1], 10);
    const mm = m[2];
    if (h >= 0 && h <= 9 && parseInt(mm, 10) >= 0 && parseInt(mm, 10) <= 59) {
      return `${h.toString().padStart(2, "0")}:${mm}`;
    }
  }
  return null;
}

/** Pure classifier. Same contract as ocr/normalizers/validator.py:classify. */
export function classify(value: string | null | undefined, field: FieldName): ClassResult {
  const text = (value ?? "").trim();

  if (!text) return { klasse: "4", normalized: null };

  if (isMarginalia(text, field)) {
    return { klasse: "5a", normalized: null, audit: { reason: "marginalia", raw_text: text } };
  }

  if (TIME_FIELDS.includes(field)) {
    if (isTimeStrict(text)) return { klasse: "1", normalized: text };
    const repaired = normalizeTime(text);
    if (repaired !== null) {
      return { klasse: "2", normalized: repaired, audit: { raw_text: text, repair: "normalize_time" } };
    }
    const canonical = isStatusCode(text);
    if (canonical) {
      return {
        klasse: "3",
        normalized: canonical,
        redirectToField: SYMB_FIELD,
        audit: { raw_text: text, redirect: `${field}->${SYMB_FIELD}` },
      };
    }
    return {
      klasse: "5b",
      normalized: null,
      audit: { raw_text: text, reason: "unknown_in_time_field" },
    };
  }

  if (field === SYMB_FIELD) {
    const canonical = isStatusCode(text);
    if (canonical) return { klasse: "1", normalized: canonical };
    if (normalizeTime(text) !== null) {
      return { klasse: "5b", normalized: null, audit: { raw_text: text, reason: "time_value_in_symb" } };
    }
    return { klasse: "5b", normalized: null, audit: { raw_text: text, reason: "unknown_status_code" } };
  }

  return { klasse: "5b", normalized: null, audit: { raw_text: text, reason: `unsupported_field:${field}` } };
}
